/* global subscribe, PUB_SUB_EVENTS, GIFT_CARD_PROPS */
if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super()
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`)
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`)
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`)
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`)
        this.messageCount = this.querySelector('[data-recipient-message-count]')
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`)
        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper')
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul')
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message')
        this.defaultErrorHeader = this.errorMessage?.innerText
        this.currentProductVariantId = this.dataset.productVariantId
        this.form = this.closest('form')

        this.onInputBound = this.onInput.bind(this)
        this.onFormDataBound = this.onFormData.bind(this)
        this.onEmailBlurBound = this.onEmailBlur.bind(this)
        this.onSendOnBlurBound = this.onSendOnBlur.bind(this)
        this.onFormSubmitBound = this.onFormSubmit.bind(this)
      }

      cartUpdateUnsubscriber = undefined
      variantChangeUnsubscriber = undefined
      cartErrorUnsubscriber = undefined

      connectedCallback() {
        this.applyLocalDateBounds()
        this.syncRecipientControl()
        this.updateMessageCount()
        this.addEventListener('input', this.onInputBound)
        this.emailInput?.addEventListener('blur', this.onEmailBlurBound)
        this.sendonInput?.addEventListener('blur', this.onSendOnBlurBound)
        this.form?.addEventListener('formdata', this.onFormDataBound)
        this.form?.addEventListener('submit', this.onFormSubmitBound, true)

        // 仅在当前商品变体成功加入购物车后重置表单。
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm()
          }
        })

        // 商品变体切换后，同步更新购物车事件所匹配的变体 ID。
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString()
          }
        })

        // 显示当前商品表单对应的 Shopify 校验错误。
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, event => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors)
          }
        })
      }

      disconnectedCallback() {
        this.removeEventListener('input', this.onInputBound)
        this.emailInput?.removeEventListener('blur', this.onEmailBlurBound)
        this.sendonInput?.removeEventListener('blur', this.onSendOnBlurBound)
        this.form?.removeEventListener('formdata', this.onFormDataBound)
        this.form?.removeEventListener('submit', this.onFormSubmitBound, true)

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber()
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber()
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber()
        }
      }

      onInput() {
        this.syncRecipientControl()
        this.updateMessageCount()
        if (this.emailInput?.getAttribute('aria-invalid') === 'true') {
          this.validateEmail(this.hasRecipientDetails())
        }
      }

      onEmailBlur() {
        if (!this.emailInput) return
        this.emailInput.value = this.emailInput.value.trim()
        this.syncRecipientControl()
        this.validateEmail(this.hasRecipientDetails())
      }

      onSendOnBlur() {
        this.validateSendOn()
      }

      onFormSubmit(event) {
        const isEmailValid = this.validateEmail(true)
        const isSendOnValid = this.validateSendOn()
        if (isEmailValid && isSendOnValid) return

        event.preventDefault()
        event.stopImmediatePropagation()
        if (!isEmailValid) {
          this.emailInput?.focus()
        }
      }

      hasRecipientDetails() {
        return [this.nameInput, this.messageInput, this.sendonInput].some(input => Boolean(input?.value.trim()))
      }

      isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      }

      validateEmail(requireEmailForRecipientDetails = false) {
        if (!this.emailInput) return true

        const value = this.emailInput.value.trim()
        if (!value) {
          if (requireEmailForRecipientDetails && this.hasRecipientDetails()) {
            this.showEmailError(this.dataset.emailRequiredError || 'Bitte gib deine E-Mail-Adresse ein')
            return false
          }

          this.clearEmailError()
          return true
        }

        if (this.isValidEmail(value)) {
          this.clearEmailError()
          return true
        }

        this.showEmailError(this.dataset.emailError || 'Bitte gib eine gültige E-Mail-Adresse ein')
        return false
      }

      formatLocalDate(date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      applyLocalDateBounds() {
        if (!this.sendonInput) return

        const today = new Date()
        const max = new Date(today)
        max.setDate(max.getDate() + 30)

        this.sendonInput.min = this.formatLocalDate(today)
        this.sendonInput.max = this.formatLocalDate(max)
      }

      validateSendOn() {
        if (!this.sendonInput) return true

        const { value } = this.sendonInput
        if (!value) {
          this.clearSendOnError()
          return true
        }

        if (this.sendonInput.min && value < this.sendonInput.min) {
          this.showSendOnError(this.dataset.dateMinError || 'Bitte wählen Sie ein zukünftiges Datum.')
          return false
        }

        if (!this.sendonInput.max || value <= this.sendonInput.max) {
          this.clearSendOnError()
          return true
        }

        this.showSendOnError(this.dataset.dateRangeError || 'Der Versand kann bis zu 30 Tage im Voraus geplant werden.')
        return false
      }

      showSendOnError(message) {
        const errorMessageId = `RecipientForm-send_on-error-${this.dataset.sectionId}`
        const errorMessageElement = this.querySelector(`#${errorMessageId}`)
        const errorTextElement = errorMessageElement?.querySelector('.error-message')
        if (errorTextElement) {
          errorTextElement.innerText = message
          errorMessageElement.classList.remove('hidden')
        }

        this.sendonInput.setAttribute('aria-invalid', 'true')
        this.sendonInput.setAttribute('aria-describedby', errorMessageId)
      }

      clearSendOnError() {
        const errorMessageId = `RecipientForm-send_on-error-${this.dataset.sectionId}`
        const errorMessageElement = this.querySelector(`#${errorMessageId}`)
        const errorTextElement = errorMessageElement?.querySelector('.error-message')
        if (errorTextElement) errorTextElement.innerText = ''
        errorMessageElement?.classList.add('hidden')

        this.sendonInput.setAttribute('aria-invalid', 'false')
        this.sendonInput.removeAttribute('aria-describedby')
      }

      showEmailError(message) {
        const errorMessageId = `RecipientForm-email-error-${this.dataset.sectionId}`
        const errorMessageElement = this.querySelector(`#${errorMessageId}`)
        const errorTextElement = errorMessageElement?.querySelector('.error-message')
        if (errorTextElement) {
          errorTextElement.innerText = `${message}.`
          errorMessageElement.classList.remove('hidden')
        }

        this.emailInput.setAttribute('aria-invalid', 'true')
        this.emailInput.setAttribute('aria-describedby', errorMessageId)
      }

      clearEmailError() {
        const errorMessageId = `RecipientForm-email-error-${this.dataset.sectionId}`
        const errorMessageElement = this.querySelector(`#${errorMessageId}`)
        const errorTextElement = errorMessageElement?.querySelector('.error-message')
        if (errorTextElement) errorTextElement.innerText = ''
        errorMessageElement?.classList.add('hidden')

        this.emailInput.setAttribute('aria-invalid', 'false')
        this.emailInput.removeAttribute('aria-describedby')
      }

      onFormData(event) {
        if (this.emailInput.value.trim()) {
          // Shopify 礼品卡配送服务通过这些私有订单项属性识别收件信息。
          event.formData.set('properties[__shopify_send_gift_card_to_recipient]', 'true')
          this.setPersistentRecipientProperties(event.formData)
        } else {
          event.formData.delete('properties[__shopify_send_gift_card_to_recipient]')
          Object.values(GIFT_CARD_PROPS).forEach(property => {
            event.formData.delete(`properties[${property}]`)
          })
        }
      }

      setPersistentRecipientProperties(formData) {
        const properties = [
          [GIFT_CARD_PROPS.RECIPIENT_EMAIL, this.emailInput],
          [GIFT_CARD_PROPS.RECIPIENT_NAME, this.nameInput],
          [GIFT_CARD_PROPS.RECIPIENT_MESSAGE, this.messageInput],
          [GIFT_CARD_PROPS.SEND_ON, this.sendonInput]
        ]

        properties.forEach(([property, input]) => {
          const value = input?.value.trim()
          if (value) {
            formData.set(`properties[${property}]`, value)
          } else {
            formData.delete(`properties[${property}]`)
          }
        })
      }

      syncRecipientControl() {
        const hasRecipient = Boolean(this.emailInput.value.trim())
        this.hiddenControlField.disabled = !hasRecipient
      }

      updateMessageCount() {
        if (this.messageCount) this.messageCount.textContent = this.messageInput.value.length.toString()
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage()
        this.errorMessageWrapper.hidden = false
        if (typeof body === 'object') {
          // 将 Shopify 返回的字段错误映射到行内提示，并补充无障碍属性。
          this.errorMessage.innerText = this.defaultErrorHeader
          Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`
            const message = Array.isArray(value) ? value.join(', ') : value
            const errorMessageElement = this.querySelector(`#${errorMessageId}`)
            const errorTextElement = errorMessageElement?.querySelector('.error-message')
            if (!errorTextElement) return

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message))
            }

            errorTextElement.innerText = `${message}.`
            errorMessageElement.classList.remove('hidden')

            const inputElement = key === 'send_on' ? this.sendonInput : this[`${key}Input`]
            if (!inputElement) return

            inputElement.setAttribute('aria-invalid', 'true')
            inputElement.setAttribute('aria-describedby', errorMessageId)
          })
          return
        }

        this.errorMessage.innerText = body || title
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li')
        const a = document.createElement('a')
        a.setAttribute('href', target)
        a.innerText = message
        li.appendChild(a)
        li.className = 'error-message'
        return li
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true

        if (this.errorMessageList) this.errorMessageList.innerHTML = ''

        this.querySelectorAll('.recipient-fields .form__message').forEach(field => {
          field.classList.add('hidden')
          const textField = field.querySelector('.error-message')
          if (textField) textField.innerText = ''
        })
        ;[this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach(inputElement => {
          inputElement.setAttribute('aria-invalid', 'false')
          inputElement.removeAttribute('aria-describedby')
        })
      }

      resetRecipientForm() {
        ;[this.emailInput, this.nameInput, this.messageInput, this.sendonInput].forEach(field => {
          field.value = ''
        })
        this.syncRecipientControl()
        this.updateMessageCount()
        this.clearErrorMessage()
      }
    }
  )
}
